import type { FeatureFlags, Tool } from '@ace-tracker/shared';
import { isViewEnabled } from '../features';
import type { ViewMode } from '../urlState';
import { PRODUCT_NAME, Wordmark } from './Wordmark';

export const TOOL_LABELS: Record<Tool, string> = {
  copilot: 'GitHub Copilot',
  'claude-code': 'Claude Code',
  codex: 'OpenAI Codex',
  kiro: 'AWS Kiro',
  gemini: 'Gemini CLI',
  cursor: 'Cursor',
  windsurf: 'Windsurf',
};
export const VIEW_LABELS: Record<ViewMode, string> = { ...TOOL_LABELS, executive: 'Executive', overview: 'All Tools', spend: 'Spend', efficiency: 'Efficiency', sessions: 'Sessions', optimization: 'Token Optimization', rtk: 'RTK', headroom: 'Headroom', graphify: 'Graphify', health: 'Data Health', settings: 'Settings' };

const OPTIMIZER_VIEWS: ViewMode[] = ['rtk', 'headroom', 'graphify'];

const VIEW_ICONS: Record<ViewMode, string> = {
  executive: 'fa-solid fa-chart-line',
  overview: 'fa-solid fa-layer-group',
  spend: 'fa-solid fa-coins',
  efficiency: 'fa-solid fa-gauge-high',
  sessions: 'fa-solid fa-list-check',
  copilot: 'fa-brands fa-github',
  'claude-code': 'fa-solid fa-terminal',
  codex: 'fa-solid fa-code',
  kiro: 'fa-solid fa-ghost',
  gemini: 'fa-solid fa-star',
  cursor: 'fa-solid fa-i-cursor',
  windsurf: 'fa-solid fa-wind',
  optimization: 'fa-solid fa-compress',
  rtk: 'fa-solid fa-filter',
  headroom: 'fa-solid fa-minimize',
  graphify: 'fa-solid fa-diagram-project',
  health: 'fa-solid fa-heart-pulse',
  settings: 'fa-solid fa-gear',
};

const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sky-blue';

/**
 * Left navigation: the view switch (Executive summary, All Tools overview,
 * then one entry per tool) at the top; Settings and account controls pinned to the bottom. Active state is carried by a left marker bar
 * and font weight as well as color (design system §5: never color alone),
 * and by aria-current for assistive tech.
 */
export function Sidebar({
  view,
  onViewChange,
  collapsed,
  onToggleCollapsed,
  username,
  onLogout,
  flags,
}: {
  view: ViewMode;
  /** Admin feature flags (Settings → Feature flags): a flagged-off tool or optimizer is not listed. */
  flags: FeatureFlags;
  onViewChange: (view: ViewMode) => void;
  /** Icon-only rail. Ignored in the mobile drawer, which is always expanded. */
  collapsed: boolean;
  /** Omitted in the mobile drawer, where there's nothing to collapse to. */
  onToggleCollapsed?: () => void;
  username: string | null;
  onLogout: () => void;
}) {
  const tools = (Object.keys(TOOL_LABELS) as Tool[]).filter((t) => isViewEnabled(t, flags));
  const optimizers = OPTIMIZER_VIEWS.filter((o) => isViewEnabled(o, flags));
  const collapseButton = onToggleCollapsed && (
    <button
      type="button"
      onClick={onToggleCollapsed}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-input text-text-muted transition-colors duration-150 hover:bg-surface-alt hover:text-text-default ${focusRing}`}
    >
      <i aria-hidden="true" className={`fa-solid ${collapsed ? 'fa-angles-right' : 'fa-angles-left'} text-base`} />
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      <div
        className={`flex min-h-[72px] shrink-0 border-b border-border ${
          collapsed ? 'flex-col items-center justify-center gap-1 px-2 py-2' : 'items-center justify-between gap-2 pl-5 pr-3'
        }`}
      >
        {collapsed ? (
          <span className="text-base font-bold" title={PRODUCT_NAME}>
            <span className="sr-only">{PRODUCT_NAME}</span>
            <span aria-hidden="true">
              <span className="text-text-default">T</span>
              <span className="text-primary-blue">i</span>
            </span>
          </span>
        ) : (
          <span className="min-w-0 leading-tight">
            <Wordmark className="block text-lg" />
            <span className="block text-xs font-normal text-text-faint">AI Adoption Insights</span>
          </span>
        )}
        {collapseButton}
      </div>

      <nav aria-label="Dashboard views" className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5">
          <NavItem view="executive" active={view === 'executive'} collapsed={collapsed} onSelect={onViewChange} />
          <NavItem view="overview" active={view === 'overview'} collapsed={collapsed} onSelect={onViewChange} />
          {isViewEnabled('spend', flags) && (
            <NavItem view="spend" active={view === 'spend'} collapsed={collapsed} onSelect={onViewChange} />
          )}
          {isViewEnabled('efficiency', flags) && (
            <NavItem view="efficiency" active={view === 'efficiency'} collapsed={collapsed} onSelect={onViewChange} />
          )}
          {isViewEnabled('sessions', flags) && (
            <NavItem view="sessions" active={view === 'sessions'} collapsed={collapsed} onSelect={onViewChange} />
          )}
          {isViewEnabled('optimization', flags) && (
            <NavItem view="optimization" active={view === 'optimization'} collapsed={collapsed} onSelect={onViewChange} />
          )}
        </ul>
        {tools.length > 0 && (
          <>
            {!collapsed && (
              <p className="px-5 pb-1.5 pt-4 text-[11px] font-medium uppercase tracking-[0.9px] text-text-faint">Tools</p>
            )}
            {collapsed && <hr className="mx-3 my-3 border-border" />}
            <ul className="space-y-0.5">
              {tools.map((t) => (
                <NavItem key={t} view={t} active={view === t} collapsed={collapsed} onSelect={onViewChange} />
              ))}
            </ul>
          </>
        )}
        {optimizers.length > 0 && (
          <>
            {!collapsed && (
              <p className="px-5 pb-1.5 pt-4 text-[11px] font-medium uppercase tracking-[0.9px] text-text-faint">Optimizers</p>
            )}
            {collapsed && <hr className="mx-3 my-3 border-border" />}
            <ul className="space-y-0.5">
              {optimizers.map((o) => (
                <NavItem key={o} view={o} active={view === o} collapsed={collapsed} onSelect={onViewChange} />
              ))}
            </ul>
          </>
        )}
      </nav>

      <div className="space-y-0.5 border-t border-border py-3">
        <ul>
          <NavItem view="health" active={view === 'health'} collapsed={collapsed} onSelect={onViewChange} />
          <NavItem view="settings" active={view === 'settings'} collapsed={collapsed} onSelect={onViewChange} />
        </ul>
        <div
          className={`flex min-h-[44px] items-center text-sm text-text-muted ${
            collapsed ? 'flex-col justify-center gap-1 px-2' : 'gap-3 pl-5 pr-3'
          }`}
        >
          {username && (
            <span
              className={`flex min-w-0 items-center gap-3 ${collapsed ? 'min-h-[44px] justify-center' : 'flex-1'}`}
              title={collapsed ? `Signed in as ${username}` : undefined}
            >
              <i aria-hidden="true" className="fa-solid fa-circle-user w-4 text-center text-base" />
              <span className={collapsed ? 'sr-only' : 'truncate font-medium text-text-default'}>{username}</span>
            </span>
          )}
          <button
            type="button"
            onClick={onLogout}
            aria-label="Sign out"
            title="Sign out"
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-input text-text-muted transition-colors duration-150 hover:bg-surface-alt hover:text-text-default ${focusRing} ${
              collapsed ? '' : 'ml-auto'
            }`}
          >
            <i aria-hidden="true" className="fa-solid fa-right-from-bracket text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}

function NavItem({
  view,
  active,
  collapsed,
  onSelect,
}: {
  view: ViewMode;
  active: boolean;
  collapsed: boolean;
  onSelect: (view: ViewMode) => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-current={active ? 'page' : undefined}
        title={collapsed ? VIEW_LABELS[view] : undefined}
        onClick={() => onSelect(view)}
        className={`relative flex min-h-[44px] w-full items-center gap-3 text-left text-sm tracking-[0.2px] transition-colors duration-150 ${
          collapsed ? 'justify-center px-2' : 'px-5'
        } ${focusRing} ${
          active
            ? 'bg-surface-alt font-bold text-primary-blue before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-r before:bg-primary-blue'
            : 'text-text-muted hover:bg-surface-alt hover:text-text-default'
        }`}
      >
        <i aria-hidden="true" className={`${VIEW_ICONS[view]} w-4 text-center text-base`} />
        <span className={collapsed ? 'sr-only' : ''}>{VIEW_LABELS[view]}</span>
      </button>
    </li>
  );
}
